import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useSeason } from '../contexts/SeasonContext';
import PageHeader from '../components/PageHeader';
import TabNav from '../components/TabNav';
import ScrollReveal from '../components/ScrollReveal';
import PlanningCreneaux, { groupCreneauxByJour } from '../components/PlanningCreneaux';
import { FIXED_H } from '../components/layoutConstants';

const tabDefs = [
  { key: 'creneauxAdultes',      label: 'Adulte',                      eyebrow: 'Créneaux',           title: 'Adulte'                     },
  { key: 'creneauxJeunes',       label: 'Jeunes',                      eyebrow: 'Créneaux',           title: 'Jeunes'                     },
  { key: 'CategoriesDefinition', label: 'Définition des catégories',   eyebrow: 'Catégories FFBaD',   title: 'Définition des Catégories'  },
];

const DefinitionCategories = ({ seasonEndYear }) => {
  const y = seasonEndYear || new Date().getFullYear();
  const categories = [
    { name: 'Minibad 1, 2', value: `${y - 7} - ${y - 6}` }, { name: 'Poussin 1',  value: `${y - 8}` },
    { name: 'Poussin 2',    value: `${y - 9}` },             { name: 'Benjamin 1', value: `${y - 10}` },
    { name: 'Benjamin 2',   value: `${y - 11}` },            { name: 'Minime 1',   value: `${y - 12}` },
    { name: 'Minime 2',     value: `${y - 13}` },            { name: 'Cadet 1',    value: `${y - 14}` },
    { name: 'Cadet 2',      value: `${y - 15}` },            { name: 'Junior 1',   value: `${y - 16}` },
    { name: 'Junior 2',     value: `${y - 17}` },            { name: 'Sénior',     value: `≤ ${y - 18}` },
    { name: 'Vétéran 1',   value: `${y - 39} - ${y - 35}` },{ name: 'Vétéran 2',  value: `${y - 44} - ${y - 40}` },
    { name: 'Vétéran 3',   value: `${y - 49} - ${y - 45}` },{ name: 'Vétéran 4',  value: `${y - 54} - ${y - 50}` },
    { name: 'Vétéran 5',   value: `${y - 59} - ${y - 55}` },{ name: 'Vétéran 6',  value: `${y - 64} - ${y - 60}` },
    { name: 'Vétéran 7',   value: `${y - 69} - ${y - 65}` },{ name: 'Vétéran 8',  value: `≤ ${y - 70}` },
  ];
  return (
    <div className="overflow-x-auto rounded-2xl border border-stone-100 shadow-card">
      <table className="w-full bg-white border-collapse">
        <thead>
          <tr style={{ background: 'var(--gradient-brand)' }}>
            <th className="py-5 px-8 text-left font-semibold text-white text-sm tracking-wide w-1/2">Catégorie d'âge</th>
            <th className="py-5 px-8 text-left font-semibold text-white text-sm tracking-wide w-1/2">Année de naissance</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat, i) => (
            <tr key={i} className={`border-b border-stone-50 hover:bg-brand-50/40 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}>
              <td className="py-4 px-8 border-r border-stone-100"><span className="font-medium text-stone-800">{cat.name}</span></td>
              <td className="py-4 px-8 text-stone-600 font-mono text-sm">{cat.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const VacancesNotice = () => (
  <div className="flex gap-3 p-5 rounded-2xl bg-amber-50 border border-amber-100 max-w-3xl mt-8">
    <FaExclamationTriangle className="text-amber-500 shrink-0 mt-0.5" size={15} />
    <div className="text-sm text-amber-800 leading-relaxed space-y-1">
      <p className="font-semibold">Attention — Vacances scolaires</p>
      <p>Ces créneaux ne sont pas valables pendant les vacances scolaires. Consultez nos communications pour les horaires adaptés.</p>
      <p className="font-semibold">Les volants ne sont pas fournis. Chaque joueur doit apporter les siens.</p>
    </div>
  </div>
);

const Creneaux = ({ setCurrentSection, currentSection }) => {
  const { tab }     = useParams();
  const navigate    = useNavigate();
  const stickyRef   = useRef(null);
  const sectionRefs = useRef({});
  const activeRef   = useRef(null);
  const isProg      = useRef(false);
  const [active, setActive] = useState(tab || 'creneauxAdultes');

  const { data: creneaux, loading } = useApi('creneaux');
  const { season, seasonLabel } = useSeason();
  const seasonEndYear = season?.end_date ? new Date(season.end_date).getFullYear() : new Date().getFullYear();

  const adultesJours = groupCreneauxByJour(creneaux, 'adulte');
  const jeunesJours  = groupCreneauxByJour(creneaux, 'jeunes');

  const scrollOffset = useCallback(() => FIXED_H + (stickyRef.current?.offsetHeight ?? 0), []);

  const goTo = useCallback((key, behavior = 'smooth') => {
    const el = sectionRefs.current[key];
    if (!el) return;
    isProg.current = true;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - scrollOffset(), behavior });
    clearTimeout(goTo._t);
    goTo._t = setTimeout(() => { isProg.current = false; }, 900);
    setActive(key);
    navigate(`/creneaux/${key}`, { replace: true });
  }, [navigate, scrollOffset]);

  useEffect(() => {
    const key = tab || 'creneauxAdultes';
    if (key !== 'creneauxAdultes') { const t = setTimeout(() => goTo(key, 'instant'), 120); return () => clearTimeout(t); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const onScroll = () => {
      if (isProg.current) return;
      const threshold = scrollOffset() + 40;
      let current = tabDefs[0].key;
      for (const { key } of tabDefs) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top <= threshold) current = key;
      }
      if (current !== activeRef.current) { activeRef.current = current; setActive(current); navigate(`/creneaux/${current}`, { replace: true }); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navigate, scrollOffset]);

  const ref = (key) => (el) => { sectionRefs.current[key] = el; };
  const meta = tabDefs.find(t => t.key === active);

  return (
    <>
      <PageHeader title="Nos Créneaux" subtitle={`Horaires et tarifs pour la saison ${seasonLabel || seasonEndYear}`}
        setCurrentSection={setCurrentSection} currentSection={currentSection} />

      <div ref={stickyRef} className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]" style={{ top: FIXED_H }}>
        <div className="max-w-6xl mx-auto px-5 md:pl-8 py-3 flex items-center gap-3 min-h-[52px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }} className="flex items-center gap-3">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-brand-800/60">{meta?.eyebrow}</span>
              <div className="w-px h-4 bg-stone-200 shrink-0" />
              <span className="text-sm font-bold text-stone-950">{meta?.title}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="border-t border-stone-100">
          <div className="max-w-6xl mx-auto px-5 md:pl-8">
            <TabNav tabs={tabDefs} activeTab={active} onTabChange={goTo} />
          </div>
        </div>
      </div>

      {/* Adulte */}
      <div ref={ref('creneauxAdultes')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto space-y-8">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Créneaux</span><h2 className="display-md">Adulte</h2></ScrollReveal>
            <ScrollReveal>
              {loading
                ? <div className="py-12 text-center text-stone-400 text-sm">Chargement…</div>
                : adultesJours.length
                  ? <PlanningCreneaux jours={adultesJours} />
                  : <p className="text-stone-400 text-sm py-8 text-center">Aucun créneau adulte configuré pour le moment.</p>
              }
            </ScrollReveal>
            {!loading && <VacancesNotice />}
          </div>
        </section>
      </div>

      {/* Jeunes */}
      <div ref={ref('creneauxJeunes')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto space-y-8">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Créneaux</span><h2 className="display-md">Jeunes</h2></ScrollReveal>
            <ScrollReveal>
              {loading
                ? <div className="py-12 text-center text-stone-400 text-sm">Chargement…</div>
                : jeunesJours.length
                  ? <PlanningCreneaux jours={jeunesJours} />
                  : <p className="text-stone-400 text-sm py-8 text-center">Aucun créneau jeunes configuré pour le moment.</p>
              }
            </ScrollReveal>
            {!loading && <VacancesNotice />}
          </div>
        </section>
      </div>

      {/* Définition des catégories */}
      <div ref={ref('CategoriesDefinition')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8"><span className="eyebrow mb-3 block">Catégories FFBaD</span><h2 className="display-md">Définition des Catégories</h2></ScrollReveal>
            <DefinitionCategories seasonEndYear={seasonEndYear} />
          </div>
        </section>
      </div>
    </>
  );
};

export default Creneaux;
